"use client";
import {
  type PlainDate,
  type PlainDateString,
  getCurrentDate,
  toPlainDateString,
} from "@sa-nu/shared-utils/date";
import clsx from "clsx";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";

import { DayOfWeekHeader } from "./_components/day-of-week-header";
import { MonthSection } from "./_components/month-section";
import { UnavailablePeriodNote } from "./_components/unavailable-period-note";
import { computeScrollTopToCenterElement } from "./_utils/compute-scroll-top-to-center-element";
import { deriveCheckoutTooltipDate } from "./_utils/derive-checkout-tooltip-date";
import type { DisabledAppearance } from "./_utils/derive-date-cell-state";
import {
  type DatePickerSelection,
  normalizeSelection,
} from "./_utils/get-next-selection";
import { resolveInitialScrollDate } from "./_utils/resolve-initial-scroll-date";
import {
  type UnavailablePeriod,
  buildMonthItems,
} from "./_utils/unavailable-periods";

// scrollIntoView は祖先 (Drawer やページ全体) までスクロールさせる恐れがあるため、scrollTop を直接合わせる。
function scrollMonthUnderHeader(
  container: HTMLDivElement,
  monthEl: HTMLDivElement,
  dayOfWeekHeaderHeight: number,
) {
  container.scrollTop =
    monthEl.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop -
    dayOfWeekHeaderHeight;
}

export type { DatePickerSelection, UnavailablePeriod };

export type DatePickerDateInfo = {
  price?: string;
  disabled?: boolean;
  /** disabled 時の見た目。"muted" (default) = 従来のグレーアウト / "normal" = 通常表示のまま非インタラクティブ */
  disabledAppearance?: DisabledAppearance;
};

const JA_MESSAGES = {
  dayOfWeekLabels: ["日", "月", "火", "水", "木", "金", "土"],
  checkoutTooltip: "チェックアウト日を選択",
} as const;

export type DatePickerMessages = typeof JA_MESSAGES;

export type DatePickerProps = {
  minDate: PlainDate;
  maxDate: PlainDate;
  /**
   * グリッド描画の上限。selectable な maxDate より後の日も「描画だけ」したいときに指定する。
   * 例: CO 日(=最終泊+1)が翌週にずれる場合に、その週を常に描画して CO セルを見せる。
   * 選択可否は maxDate が決めるため、maxDate より後の描画分は非選択(グレー)になる。省略時は maxDate。
   */
  renderMaxDate?: PlainDate;
  startDate: PlainDate | null;
  endDate: PlainDate | null;
  /**
   * 日程未選択のときにマウント時のスクロール位置を合わせる日。省略時は先頭 (minDate の月) 表示。
   * 描画範囲外を指した場合は範囲内にクランプされる。
   */
  initialVisibleDate?: PlainDate | null;
  onChange: (next: DatePickerSelection) => void;
  maxNights?: number;
  dateInfoMap?: Record<PlainDateString, DatePickerDateInfo>;
  today?: PlainDate;
  messages?: DatePickerMessages;
  /**
   * 予約できない期間 (穴あき)。期間に丸ごと含まれる月は描画せず、代わりに note を 1 つ表示する。
   * 期間内の日は選択不可 (CO 選択中は期間の初日のみ CO 可)。
   */
  unavailablePeriods?: readonly UnavailablePeriod[];
  /** large (既定) = ゆったり / small = コンパクト。ヘッダー周りのサイズのみ変わる */
  size?: "large" | "small";
  /**
   * true(既定) = 端でスクロールチェイニングを止める(Popover/Drawer で背景スクロールを防ぐ)。
   * false = 親へのスクロール伝播を許可する(モーダル本体スクロールに埋め込む場合)。
   */
  containScroll?: boolean;
  /** sticky な曜日/月見出しの背景にも適用し、スクロール時に下の日付セルが透けないようにする */
  background?: "base" | "subtle";
  /** DatePicker に重ねる footer の高さ分を指定すると、最後の日付セルが footer に隠れずスクロールし切れる */
  bottomPadding?: number;
  /** マウント後にこの日が変わるたび、そのセルを可視領域の中央へスムーズスクロールする。マウント時と null への変化では何もしない */
  centeredVisibleDate?: PlainDate | null;
};

/**
 * DX 用 DatePicker。全月を縦に並べ、sticky な曜日ヘッダーと月見出しを持つ縦スクロール表示。
 * マウント時に startDate (未選択時は initialVisibleDate) の月へスクロールを合わせる。
 */
export function DatePicker({
  minDate,
  maxDate,
  renderMaxDate,
  startDate,
  endDate,
  initialVisibleDate,
  onChange,
  maxNights,
  dateInfoMap,
  today = getCurrentDate(),
  messages = JA_MESSAGES,
  unavailablePeriods,
  size = "large",
  containScroll = true,
  background = "base",
  bottomPadding = 0,
  centeredVisibleDate = null,
}: DatePickerProps) {
  const monthItems = useMemo(
    () =>
      buildMonthItems({
        minDate,
        renderMaxDate: renderMaxDate ?? maxDate,
        unavailablePeriods: unavailablePeriods ?? [],
      }),
    [minDate, maxDate, renderMaxDate, unavailablePeriods],
  );

  // sticky 曜日ヘッダーの高さ (pt + 32px)。large: pt-[12px]=44px / small: pt-[8px]=40px
  // 月見出しの sticky top と初期スクロール・中央寄せスクロールの計算に使う
  const dayOfWeekHeaderHeight = size === "small" ? 40 : 44;

  const containerRef = useRef<HTMLDivElement>(null);
  const monthSectionRefs = useRef(new Map<string, HTMLDivElement>());
  // 指定した月のキーすべてに要素を登録する ref callback を返す
  const registerSection = (keys: string[]) => (el: HTMLDivElement | null) => {
    keys.forEach((key) => {
      if (el !== null) {
        // 同じ年月が 2 回出る場合は先に出る方を初期スクロールの対象にする
        if (!monthSectionRefs.current.has(key)) {
          monthSectionRefs.current.set(key, el);
        }
      } else {
        monthSectionRefs.current.delete(key);
      }
    });
  };
  // マウント時のスクロール対象日を保持 (マウント後の変更で再スクロールしてユーザーの
  // スクロール位置を奪わないため)
  const initialScrollDateRef = useRef(
    resolveInitialScrollDate({
      startDate,
      initialVisibleDate,
      minDate,
      renderMaxDate: renderMaxDate ?? maxDate,
    }),
  );

  useLayoutEffect(() => {
    const initialScrollDate = initialScrollDateRef.current;
    if (initialScrollDate === null) return;
    const container = containerRef.current;
    const monthEl = monthSectionRefs.current.get(
      initialScrollDate.toPlainYearMonth().toString(),
    );
    if (container === null || monthEl === undefined) return;
    scrollMonthUnderHeader(container, monthEl, dayOfWeekHeaderHeight);
  }, [dayOfWeekHeaderHeight]);

  const centeredVisibleDateKey =
    centeredVisibleDate !== null
      ? toPlainDateString(centeredVisibleDate)
      : null;
  const centeredVisibleMonthKey =
    centeredVisibleDate?.toPlainYearMonth().toString() ?? null;
  const previousCenteredVisibleDateKeyRef = useRef(centeredVisibleDateKey);
  // マウント後は再スクロールしない方針の例外。呼び出し元が明示的に求めたときだけ動かす。
  useEffect(() => {
    const previousKey = previousCenteredVisibleDateKeyRef.current;
    previousCenteredVisibleDateKeyRef.current = centeredVisibleDateKey;
    if (
      centeredVisibleDateKey === null ||
      centeredVisibleMonthKey === null ||
      centeredVisibleDateKey === previousKey
    ) {
      return;
    }
    const container = containerRef.current;
    const monthHeading = monthSectionRefs.current
      .get(centeredVisibleMonthKey)
      ?.querySelector<HTMLElement>("[data-month-heading]");
    const cell = container?.querySelector<HTMLElement>(
      `[data-date="${centeredVisibleDateKey}"]`,
    );
    if (container == null || monthHeading == null || cell == null) {
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    container.scrollTo({
      top: computeScrollTopToCenterElement({
        containerScrollTop: container.scrollTop,
        containerTop: containerRect.top,
        containerClientHeight: container.clientHeight,
        containerScrollHeight: container.scrollHeight,
        stickyHeaderHeight:
          dayOfWeekHeaderHeight + monthHeading.getBoundingClientRect().height,
        bottomOverlayHeight: bottomPadding,
        targetTop: cellRect.top,
        targetHeight: cellRect.height,
      }),
      behavior: "smooth",
    });
  }, [
    centeredVisibleDateKey,
    centeredVisibleMonthKey,
    dayOfWeekHeaderHeight,
    bottomPadding,
  ]);

  // 価格が1つでもあれば全セルに価格行のスペースを確保し、高さ (帯の太さ・数字の縦位置) を揃える
  const showPriceRow =
    dateInfoMap !== undefined &&
    Object.values(dateInfoMap).some((info) => info.price !== undefined);

  // 不正な selection props (endDate のみ / endDate <= startDate) を正規化し、
  // 描画とクリック処理の両方に同じ値を渡す
  const selection = normalizeSelection(startDate, endDate);

  const tooltipTargetDate = deriveCheckoutTooltipDate({ selection, maxNights });

  const backgroundClass =
    background === "subtle" ? "bg-dx-surface-subtle" : "bg-dx-surface-base";
  // 注記の既定背景は subtle なので、カレンダー背景と同色になると埋もれる。そのときだけ base に反転させる
  const noteBackgroundClass =
    background === "subtle" ? "bg-dx-surface-base" : undefined;

  return (
    <div
      ref={containerRef}
      className={clsx(
        backgroundClass,
        "h-full w-full overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        containScroll ? "overscroll-contain" : "overscroll-auto",
      )}
    >
      {/* 曜日ヘッダー: sticky */}
      <div
        className={clsx(
          backgroundClass,
          "sticky top-0 z-10 px-[4px]",
          size === "small" ? "pt-[8px]" : "pt-[12px]",
        )}
      >
        <DayOfWeekHeader labels={messages.dayOfWeekLabels} />
      </div>

      {/* 月リスト */}
      <div className="px-[4px]" style={{ paddingBottom: bottomPadding }}>
        {monthItems.map((item) => {
          switch (item.type) {
            case "note":
              return (
                <UnavailablePeriodNote
                  key={item.key}
                  note={item.note}
                  size={size}
                  backgroundClass={noteBackgroundClass}
                  // 省略された月へのスクロール指定は、その月の位置にある注記へ向ける
                  sectionRef={registerSection(
                    item.omittedYearMonths.map((ym) => ym.toString()),
                  )}
                />
              );
            case "month":
              return (
                <MonthSection
                  key={item.key}
                  grid={item.grid}
                  size={size}
                  backgroundClass={backgroundClass}
                  dayOfWeekHeaderHeight={dayOfWeekHeaderHeight}
                  sectionRef={registerSection([item.grid.yearMonth.toString()])}
                  selection={selection}
                  today={today}
                  minDate={minDate}
                  maxDate={maxDate}
                  maxNights={maxNights}
                  dateInfoMap={dateInfoMap}
                  unavailablePeriods={unavailablePeriods}
                  showPriceRow={showPriceRow}
                  onChange={onChange}
                  tooltipTargetDate={tooltipTargetDate}
                  tooltipMessage={messages.checkoutTooltip}
                />
              );
          }
        })}
      </div>
    </div>
  );
}
